<script setup lang="ts">
import { onMounted, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import {
  BaseGrid,
  BaseGridCol,
  BaseInputText,
  BaseDivisor,
  BaseBannerAlert,
  BaseInputPassword,
  BaseSelectBox
} from '@cplug/design-system/components'
import { BannerAlertType } from '@cplug/design-system/components/props'
import type { BaseError } from '@cplug/frontend-core/models'
import type { UserConfirmation } from '../model/UserConfirmation'
import useUserStore from '~/domain/dashboard/user/users/stores/useUserStore'
import { useErrorMessages } from '~/shared/composables/useErrorMessages'
import type { User } from '~/domain/shared/user/model/User'

const modelValue = defineModel<UserConfirmation>({ required: true })
const errors = defineModel<BaseError[]>('errors', { default: () => [] })
const isValid = defineModel<boolean>('isValid', { default: false })

const props = defineProps<{
  confirmPhrase: string
}>()

const userStore = useUserStore()
const { pageList: userList } = storeToRefs(userStore)
const { t } = useI18n()

const { errorMessages } = useErrorMessages(errors, {
  userId: 'user_id',
  password: 'password'
})

// Customiza a mensagem para sempre mostrar "InvalidPassword"
const customPasswordErrors = computed(() =>
  (errorMessages.value.password?.length ?? 0) > 0 ? [t('InvalidPassword')] : []
)

const customUserIdErrors = computed(() =>
  (errorMessages.value.userId?.length ?? 0) > 0 ? errorMessages.value.userId : []
)

watch(customPasswordErrors, (newErrors) => {
  if (newErrors.length > 0) {
    modelValue.value.password = ''
  }
})

watch(
  modelValue,
  () => {
    isValid.value =
      modelValue.value.userId > 0 &&
      modelValue.value.password.length > 0 &&
      modelValue.value.confirmPhrase.length > 0 &&
      modelValue.value.confirmPhrase === props.confirmPhrase
  },
  { deep: true }
)

onMounted(() => {
  if (userList.value == null || userList.value.length === 0) {
    userStore.getAll()
  }
})
</script>

<template>
  <div class="flex flex-1 flex-col gap-6 w-full">
    <BaseGrid>
      <slot name="before-confirm-phrase" />
      <BaseGridCol cols="12">
        <BaseInputText
          v-model="modelValue.confirmPhrase"
          :title="t('ConfirmPhrase', { confirmPhrase: props.confirmPhrase })"
          data-testid="confirm-phrase-input"
        />
      </BaseGridCol>
    </BaseGrid>
    <BaseDivisor />
    <BaseGrid>
      <BaseGridCol cols="12">
        <BaseBannerAlert
          :title="t('Attention')"
          :body-text="t('ConfirmationDescription')"
          :type="BannerAlertType.Critical"
          :dismiss="false"
        />
      </BaseGridCol>
      <BaseGridCol cols="6">
        <BaseSelectBox
          v-model="modelValue.userId"
          :options="
            (userList ?? []).map((user: User) => ({
              label: user.name,
              value: user.id
            }))
          "
          :title="t('User')"
          :errors="customUserIdErrors"
        />
      </BaseGridCol>
      <BaseGridCol cols="6">
        <BaseInputPassword
          v-model="modelValue.password"
          :title="t('Password')"
          placeholder="******"
          test-id="password-input"
          :errors="customPasswordErrors"
        />
      </BaseGridCol>
    </BaseGrid>
  </div>
</template>

<i18n lang="yaml">
pt-br:
  Attention: Esta ação não pode ser desfeita!
  ConfirmPhrase: Para confirmar, digite "{confirmPhrase}" no campo abaixo.
  InvalidPassword: Senha inválida
  ConfirmationDescription: Para concluir essa ação, informe um usuário com permissões de administrador e sua senha.
  User: Usuário
  Password: Senha
</i18n>
